
































































import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import statsService from '@/api/stats.service';
import LineChartWrapper from '@/components/Vis/D3/Charts/LineChartWrapper.vue';
import LineLoader from '@/components/UI/Loader/LineLoader.vue';
import { ISentimentTrend } from '@/store/types';
import { scaleLinear, timeDay, timeMonth } from 'd3';
import { linearRegression } from 'simple-statistics';
import ShortListLoader from '@/components/UI/Loader/ShortListLoader.vue';
import SmallListLoader from '@/components/UI/Loader/SmallListLoader.vue';

const sentimentScale = scaleLinear().domain([-2, 0.8]).range([0, 100]);

const trendMap = {
  down: {
    name: 'Down',
    description: 'trending down',
    icon: 'arrow-down',
  },
  'slight-down': {
    name: 'Slight Down',
    description: 'trending slightly down',
    icon: 'arrow-down',
  },
  up: {
    name: 'Up',
    description: 'trending up',
    icon: 'arrow-up',
  },
  'slight-up': {
    name: 'Slight Up',
    description: 'trending slightly up',
    icon: 'arrow-up',
  },
  flat: {
    name: 'Flat',
    description: 'steady',
    icon: 'minus',
  },
};

export default Vue.extend({
  props: {
    cancelDashboardVersion: Boolean,
  },
  components: {
    LineChartWrapper,
    LineLoader,
    SmallListLoader,
    ShortListLoader,
  },
  data() {
    return {
      sentimentTrends: [] as ISentimentTrend[],
      loading: false,
      xField: (d: ISentimentTrend) => d.date,
      valueAccessor: (d: ISentimentTrend) => d.sentimentScore,
      valueFormat: (d: number) => d.toFixed(0),
    };
  },
  async created() {
    this.loading = true;
    const yearAgo = timeMonth.offset(timeMonth.floor(new Date()), -12);
    const breakdown = ['month'];
    const filter = { startDate: yearAgo };
    const response = await statsService.sentimentTrends({ breakdown, filter });
    const fifteenDaysAgo = timeDay.offset(new Date(), -15);
    this.sentimentTrends = response
      .filter((item) => new Date(item.date) < fifteenDaysAgo)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    this.loading = false;
  },
  computed: {
    ...mapGetters(['confidential', 'customerHealthEnabled']),
    totalCount(): number {
      return this.sentimentTrends.reduce((sum, d) => sum + d.count, 0);
    },
    regression(): { m: number; b: number } {
      if (!this.sentimentTrends.length) return { m: 0, b: 0 };
      // x y pairs as [x,y]
      const seriesPairs = this.sentimentTrends.map((d) => [new Date(d.date).getTime(), sentimentScale(d.sentimentScore)]);
      const regression = linearRegression(seriesPairs);
      return regression;
    },
    currentSentiment(): number {
      if (!this.trendline.length) return 0;
      return this.trendline[this.trendline.length - 1].sentimentScore;
    },
    m(): number {
      const millisecondsPerMonth = 1000 * 60 * 60 * 24 * 30;
      if (!this.trendline.length) return 0;
      const { m } = this.regression;
      return m * millisecondsPerMonth;
    },
    trendCategory(): 'down' | 'slight-down' | 'up' | 'slight-up' | 'flat' {
      const m = this.m;
      if (m < -0.75) return 'down';
      if (m < -0.25) return 'slight-down';
      if (m > 0.75) return 'up';
      if (m > 0.25) return 'slight-up';
      return 'flat';
    },
    trendInfo(): { label: string; icon: string } {
      return trendMap[this.trendCategory] as any;
    },
    trendline(): any {
      if (!this.sentimentTrends.length) return [];
      // x y pairs as [x,y]
      const seriesPairs = this.sentimentTrends.map((d) => [new Date(d.date).getTime(), sentimentScale(d.sentimentScore)]);
      const regression = this.regression;
      // just two points for beginning and end of trendline, based on regression
      const { m, b } = regression;
      const x1 = seriesPairs[0][0];
      const x2 = seriesPairs[seriesPairs.length - 1][0];
      const y1 = m * x1 + b;
      const y2 = m * x2 + b;
      return [
        {
          date: new Date(x1),
          sentimentScore: y1,
        },
        {
          date: new Date(x2),
          sentimentScore: y2,
        },
      ];
    },
    averageSeries(): any[] {
      return [
        {
          name: 'Average',
          data: this.sentimentTrends.map((d) => ({
            date: new Date(d.date),
            sentimentScore: sentimentScale(-0.35),
          })),
          label: 'Average',
          color: '#00000088',
          strokeDasharray: '15,0',
        },
      ];
    },
    series(): any[] {
      if (!this.sentimentTrends.length) return [];
      return [
        ...(this.trendline.length
          ? [
            {
              name: 'Trendline',
              data: this.trendline,
              label: '',
              color: '#435BC8aa',
              strokeDasharray: '15,5',
            },
          ]
          : []),
        ...this.averageSeries,
        {
          name: 'Sentiment Score',
          data: this.sentimentTrends.map((d) => ({
            date: new Date(d.date),
            sentimentScore: sentimentScale(d.sentimentScore),
          })),
          // @ts-ignore
          label: this.confidential ? 'Acme, Inc.' : this.$store.state.org.name || 'Your company',
          color: '#435BC8',
        },
      ];
    },
  },
});
